import React from "react";
import { styled } from "@mui/material/styles";
import { withStyles } from "@mui/styles";
import { useDispatch } from "react-redux";

import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

import ActivityContent from "../Activity/ActivityContent";
import ScopeOfWorkContent from "../ScopeOfWork/ScopeOfWorkContent";
import DevelopmentNeedContent from "../PDP/DevelopmentNeedContent";
import AchievementContent from "../Achievement/AchievementContent";
import QualificationContent from "../Qualification/QualificationContent";
import AppraisalContent from "../Appraisal/AppraisalContent";
import DefaultContent from "./DefaultContent";

import { Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StatusIcon from "../Icon/StatusIcon";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import SummaryContent from "../Summary/SummaryContent";

import { updateAppraisalFormElement } from "../../../../actions/rmsActions";
import { clearErrors } from "../../../../actions/commonActions";
import ErrorBoundary from "../../../Errors/ErrorBoundary";

const styles = (theme) => ({
    root: {
        width: "100%",
    },
    button: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    actionsContainer: {
        marginBottom: theme.spacing(2),
    },
    resetContainer: {
        padding: theme.spacing(3),
    },
});

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
        borderBottom: 0,
    },
    "&:before": {
        display: "none",
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "1rem" }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, .05)"
            : "rgba(0, 0, 0, .03)",
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
        transform: "rotate(90deg)",
    },
    "& .MuiAccordionSummary-content": {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const SegmentRender = ({
   segment,
   appraisalId,
   appraiseeUUID,
   isAppraiser,
   container = "",
   segmentStatus = null,
   appCode,
   expanded = false,
   onExpandOrCollapse = () => {}
}) => {
    const dispatch = useDispatch();

    const heading = segment?.title ?? "Undefined";
    const index = segment?.index ?? 0;
    const type = segment?.type ?? "default";
    let content = null;
    let helperText = null;
    let canAdd = false;

    if (segment?.helperText?.length > 0) {
        helperText = (
            <Box className="card-content">
                <Typography variant="body1" gutterBottom={true}>
                    {segment.helperText}
                </Typography>
            </Box>
        );
    }

    const updateFormField = (params) => {
        clearErrors(dispatch);
        dispatch(updateAppraisalFormElement(appraisalId, params, appCode));
    };

    switch (type) {
        case "scopeOfWork":
            content = (
                <ScopeOfWorkContent
                    segment={segment}
                    appraisalId={appraisalId}
                    onFormFieldChange={updateFormField}
                    appCode={appCode}
                />
            );
            break;

        case "qualifications":
            content = (
                <QualificationContent
                    segment={segment}
                    appraisalId={appraisalId}
                    onFormFieldChange={updateFormField}
                    appCode={appCode}
                />
            );
            break;

        case "activities":
            content = (
                <ActivityContent
                    segment={segment}
                    appraisalId={appraisalId}
                    onFormFieldChange={updateFormField}
                    appCode={appCode}
                />
            );
            break;

        case "appraisals":
            content = (
                <AppraisalContent
                    segment={segment}
                    onFormFieldChange={updateFormField}
                />
            );
            break;

        case "pdp-review":
            content = (
                <DevelopmentNeedContent
                    segment={segment}
                    appraisalId={appraisalId}
                    isAppraiser={isAppraiser}
                    appraiseeUUID={appraiseeUUID}
                    container={container}
                    onFormFieldChange={updateFormField}
                    canAdd={false}
                    appCode={appCode}
                />
            );
            break;

        case "pdp":
            canAdd = (() => {
                if (container === "outputs") {
                    switch (segment?.status) {
                        case "":
                        case "open":
                        case "incomplete":
                        case "rejected":
                        case "rejected_2":
                            return isAppraiser;
                        default:
                            return false;
                    }
                }
            })();

            content = (
                <DevelopmentNeedContent
                    segment={segment}
                    appraisalId={appraisalId}
                    isAppraiser={isAppraiser}
                    appraiseeUUID={appraiseeUUID}
                    container={container}
                    onFormFieldChange={updateFormField}
                    canAdd={canAdd}
                    appCode={appCode}
                />
            );
            break;

        case "developmentneeds":
            canAdd = (() => {
                if (container === "inputs") {
                    switch (segment?.status) {
                        case "open":
                        case "incomplete":
                        case "rejected":
                        case "rejected_1":
                        case "rejected_2":
                            return !isAppraiser;
                        default:
                            return false;
                    }
                } else if (container === "outputs") {
                    switch (segment?.status) {
                        case "":
                        case "open":
                        case "incomplete":
                        case "rejected":
                        case "rejected_2":
                            return isAppraiser;
                        default:
                            return false;
                    }
                }
            })();

            content = (
                <DevelopmentNeedContent
                    segment={segment}
                    appraisalId={appraisalId}
                    isAppraiser={isAppraiser}
                    appraiseeUUID={appraiseeUUID}
                    container={container}
                    onFormFieldChange={updateFormField}
                    canAdd={canAdd}
                    appCode={appCode}
                />
            );
            break;

        case "achievements":
            content = (
                <AchievementContent
                    segment={segment}
                    onFormFieldChange={updateFormField}
                />
            );
            break;

        case "summary":
            content = (
                <SummaryContent
                    segment={segment}
                    onFormFieldChange={updateFormField}
                />
            );
            break;

        default:
            content = (
                <DefaultContent
                    segment={segment}
                    onFormFieldChange={updateFormField}
                />
            );
            break;
    }

    let iconStatus = "";
    let iconMessage = "";

    switch (segmentStatus) {
        case "success":
            iconStatus = "success";
            iconMessage = "Completed";
            break;

        case "warning":
            iconStatus = "warning";
            iconMessage = "Required";
            break;

        case "disabled":
            iconStatus = "disabled";
            iconMessage = "OK";
            break;

        case "error":
            iconStatus = "error";
            iconMessage = "Error";
            break;

        case "info":
            iconStatus = "info";
            iconMessage = "OK";
            break;

        default:
            break;
    }

    return (
        <Accordion
            expanded={expanded}
            id={`io-segment-${container}-${index}`}
            onChange={(ev, exp) => onExpandOrCollapse(exp)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`stage-${container}-${index}-content`}
                id={`stage-${container}-${index}-header`}
            >
                <Typography variant="h3" >{heading}</Typography>
                <StatusIcon status={iconStatus} message={iconMessage} />
            </AccordionSummary>
            <AccordionDetails>
                <ErrorBoundary>
                    {helperText}
                    {content}
                </ErrorBoundary>
            </AccordionDetails>
        </Accordion>
    );
};

export default withStyles(styles)(SegmentRender);
